import { useForm } from '@tanstack/react-form';
import { endOfDay, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button, DialogFooter, Input, Label } from '@virtual-care/shadcn';
import {
  DateField,
  FieldMessage,
  FormMessage,
  TheButton,
  errorToFormIssue,
  useAction,
} from '@virtual-care/ui';

import { CreateDialog } from '../../components/create-dialog';
import {
  promoDateRangeError,
  promoDateRangeFields,
  refinePromoDateRange,
} from './schema.ts';

const schema = z
  .object({
    code: z
      .string()
      .min(2, { message: 'Code must be at least 2 characters' })
      .max(32, { message: 'Code must be 32 characters or fewer' })
      .regex(/^[A-Za-z0-9_-]+$/, {
        message: 'Only letters, digits, underscore, and hyphen are allowed',
      }),
    discountPercent: z.coerce.number().int().min(1).max(100),
    maxUses: z.coerce.number().int().min(1),
    ...promoDateRangeFields,
  })
  .refine(refinePromoDateRange, promoDateRangeError);

export function CreatePromoCode({
  trigger,
  onSave,
  ...props
}: {
  trigger: React.ReactNode;
  onSave?: () => void;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createPromoCode = useAction('POST /promo-codes', {
    invalidate: ['GET /promo-codes'],
  });

  const form = useForm({
    defaultValues: {
      code: '',
      discountPercent: 10,
      maxUses: 100,
      validFrom: '',
      validUntil: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await createPromoCode.mutateAsync({
            code: value.code.trim().toUpperCase(),
            discountPercent: value.discountPercent,
            maxUses: value.maxUses,
            validFrom: startOfDay(new Date(value.validFrom)).toISOString(),
            validUntil: endOfDay(new Date(value.validUntil)).toISOString(),
          });
          props.onOpenChange(false);
          toast.success('Promo code created');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={props.open}
      onOpenChange={props.onOpenChange}
      trigger={trigger}
      title="Create Promo Code"
      subtitle="Add a new percentage-based promo code campaign. Date range is interpreted in your local timezone — validUntil counts through end of the selected day."
      onSave={onSave}
    >
      <form.Field
        name="code"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="code">Code</Label>
            <Input
              id="code"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) =>
                field.handleChange(e.target.value.toUpperCase())
              }
              placeholder="WELCOME20"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Field
        name="discountPercent"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="discountPercent">Discount percent (1-100)</Label>
            <Input
              id="discountPercent"
              type="number"
              min={1}
              max={100}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(Number(e.target.value))}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Field
        name="maxUses"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="maxUses">Max uses (global)</Label>
            <Input
              id="maxUses"
              type="number"
              min={1}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(Number(e.target.value))}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Field
        name="validFrom"
        children={(field) => (
          <DateField
            field={field}
            label="Valid from"
            placeholder="Pick a start date"
            onBlur={field.handleBlur}
          />
        )}
      />
      <form.Field
        name="validUntil"
        children={(field) => (
          <DateField
            field={field}
            label="Valid until"
            placeholder="Pick an end date"
            onBlur={field.handleBlur}
          />
        )}
      />

      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <form.Subscribe
          selector={(state) => [state.isSubmitSuccessful]}
          children={([isSubmitSuccessful]) =>
            isSubmitSuccessful ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  props.onOpenChange(false);
                  form.reset();
                }}
              >
                Close
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => props.onOpenChange(false)}
              >
                Cancel
              </Button>
            )
          }
        />
        <form.Subscribe
          selector={(state) =>
            [
              state.canSubmit,
              state.isSubmitting,
              state.isSubmitSuccessful,
            ] as const
          }
          children={([canSubmit, isSubmitting, isSubmitSuccessful]) => (
            <TheButton
              type="submit"
              loading={isSubmitting}
              disabled={!canSubmit || isSubmitting || isSubmitSuccessful}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </TheButton>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
